import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AuditTemplate } from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, auditTypeOptions, moduleOptions, statusSeverity } from '../audit-ui';

@Component({
  selector: 'app-audit-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Setup" title="Audit Templates" subtitle="Reusable checklist templates with draft and published versions.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/templates'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <p-button label="New Template" icon="pi pi-plus" (onClick)="openTemplate()" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Templates" subtitle="Template headers, module alignment, and publish status." [resultText]="templates.length + ' templates'">
        <p-table [value]="templates" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '900px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Template</th>
              <th>Module</th>
              <th>Audit Type</th>
              <th>Language</th>
              <th>Status</th>
              <th>Active</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-template>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ template.templateName }}</strong>
                  <small>{{ template.templateCode }}</small>
                </div>
              </td>
              <td>{{ template.moduleCode || '-' }}</td>
              <td>{{ template.auditType || '-' }}</td>
              <td>{{ template.languageCode || '-' }}</td>
              <td><p-tag [value]="template.status || 'DRAFT'" [severity]="statusSeverity(template.status)"></p-tag></td>
              <td><p-tag [value]="template.active === false ? 'INACTIVE' : 'ACTIVE'" [severity]="template.active === false ? 'secondary' : 'success'"></p-tag></td>
              <td class="text-right">
                <div class="audit-row-actions">
                  <a pButton [routerLink]="['/audit/templates', template.auditTemplateId]" icon="pi pi-list-check" [text]="true" pTooltip="Builder"></a>
                  <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit header" (onClick)="openTemplate(template)" [disabled]="!canManage"></p-button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="7" class="text-center p-4">No audit templates are configured.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog
      [header]="form.auditTemplateId ? 'Edit Template Header' : 'New Template'"
      [(visible)]="dialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(860px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="template-code" [(ngModel)]="form.templateCode">
            <label for="template-code">Template code</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="template-name" [(ngModel)]="form.templateName">
            <label for="template-name">Template name</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="template-module" [options]="moduleOptions" [(ngModel)]="form.moduleCode" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="template-module">Module</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-verified"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="template-audit-type" [options]="auditTypeOptions" [(ngModel)]="form.auditType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
            <label for="template-audit-type">Audit type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-language"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="template-language" [(ngModel)]="form.languageCode">
            <label for="template-language">Language</label>
          </p-iftalabel>
        </p-inputgroup>
        <label class="audit-switch-line">
          <p-toggleswitch [(ngModel)]="form.active"></p-toggleswitch>
          <span>Active</span>
        </label>
      </div>
      <p-inputgroup class="audit-textarea-field">
        <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
        <p-iftalabel>
          <textarea pTextarea id="template-description" rows="4" [(ngModel)]="form.description"></textarea>
          <label for="template-description">Description</label>
        </p-iftalabel>
      </p-inputgroup>
      @if (formError) {
        <div class="audit-error">{{ formError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="dialogVisible = false"></p-button>
        <p-button label="Save Template" icon="pi pi-check" (onClick)="saveTemplate()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .audit-row-actions {
      display: flex;
      justify-content: flex-end;
      gap: .25rem;
    }

    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
    }

    label,
    .audit-textarea-field {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span,
    .audit-textarea-field span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select {
      width: 100%;
    }

    .audit-switch-line {
      align-items: center;
      display: flex;
      gap: .65rem;
      padding-top: 1.55rem;
    }

    .audit-textarea-field {
      margin-top: .85rem;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    @media (max-width: 900px) {
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditTemplatesComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  loading = false;
  saving = false;
  dialogVisible = false;
  formError = '';
  templates: AuditTemplate[] = [];
  form: AuditTemplate = {};

  readonly moduleOptions = moduleOptions;
  readonly auditTypeOptions = auditTypeOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.templates.manage);
  readonly statusSeverity = statusSeverity;

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Templates', value: this.templates.length, tone: 'info' },
      { label: 'Published', value: this.templates.filter(template => template.status === 'PUBLISHED').length, tone: 'success' },
      { label: 'Draft', value: this.templates.filter(template => template.status !== 'PUBLISHED').length, tone: 'warn' },
      { label: 'Modules', value: new Set(this.templates.map(template => template.moduleCode).filter(Boolean)).size }
    ];
  }

  load(): void {
    this.loading = true;
    this.auditService.templates().subscribe({
      next: response => this.templates = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Templates', detail: 'Unable to load audit templates.' }),
      complete: () => this.loading = false
    });
  }

  openTemplate(template?: AuditTemplate): void {
    this.formError = '';
    this.form = template ? { ...template } : { active: true, languageCode: 'en' };
    this.dialogVisible = true;
  }

  saveTemplate(): void {
    this.formError = '';
    if (!this.form.templateCode?.trim() || !this.form.templateName?.trim()) {
      this.formError = 'Template code and template name are required.';
      return;
    }
    const isNew = !this.form.auditTemplateId;
    this.saving = true;
    this.auditService.saveTemplate(this.form).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Template saved', detail: response.message || 'Audit template saved.' });
        this.dialogVisible = false;
        if (isNew && response.data?.auditTemplateId) {
          this.router.navigate(['/audit/templates', response.data.auditTemplateId]);
          return;
        }
        this.load();
      },
      error: error => this.formError = error?.error?.message || 'Unable to save audit template.',
      complete: () => this.saving = false
    });
  }
}
